/**
 * The issues the post-merge CI listener reports — a check that runs AFTER a merge went red.
 *
 * Reported by exactly one workflow, `.github/workflows/post-merge-red-alert.yml`, a `workflow_run`
 * listener that fires when a post-merge workflow completes on a deploy branch, through the
 * OIDC-authenticated ingest route (`apps/backend/src/issue-ingest/`). `WORKFLOW_ISSUE_GRANTS`
 * binds the code to that file and admits its `workflow_run` event, which no drift grant admits.
 *
 * PLATFORM-scoped: a branch's CI belongs to no tenant. One row per listened workflow per branch,
 * and the listener resolves it on that workflow's next green run there — so the row is the red
 * episode, and the alarm is one Slack line when the episode opens or reopens.
 */
import type { IssueDefinition } from "../issue-definition";
import { ISSUE_CATEGORY } from "../issue-enums";

export const POST_MERGE_CI_ISSUES = {
  /**
   * A post-merge check went red on a deploy branch.
   *
   * Fingerprint: `entityType: ci_workflow`, `entityId` `<branch>/<workflow file>` — e.g.
   * `staging/post-merge-checks.yml`. Per workflow because each judges a different property of the
   * tree and goes green independently; per branch because a green `main` run says nothing about a
   * red `staging` one.
   *
   * page: heavy checks moved off the pull request onto the branch, so this IS the gate now — a red
   * here is a merged change that broke something no pre-merge check looked for. Every merge on top
   * of it inherits the red and makes the culprit harder to name, so someone must act before the
   * next promotion, not when they next open the Issues page.
   */
  POST_MERGE_CHECK_RED: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "A post-merge check went red on a deploy branch. A workflow that runs after merge — not on the pull request — failed on the branch named in the entity id, so a change that already landed broke something no pre-merge check looks at. The issue payload names the red run and the commits that could have done it.",
    severity: "high",
    page: true,
    remedy:
      "Open this row on the admin Issues page (the link in the Slack line opens it directly) and read its payload: `findings` carries the red post-merge run's URL and the first-parent commits since that workflow was last green on the branch, each with its pull request and author; `run.url` is the listener run that reported it, not the red run. " +
      "Open the red run, read the failing job, and find the commit in that range that broke it — revert it on the branch, or land the fix, through a normal pull request. " +
      "Do not re-run the red workflow to make the row go away: a re-run that happens to pass on a flaky check hides a real break. " +
      "The row clears itself when that workflow's next run on that branch is green."
  }
} as const satisfies Record<string, IssueDefinition>;
